import { useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useState, useRef, useEffect } from 'react'
import { useQuery } from 'react-query'
import { 
  Home, 
  CreditCard, 
  QrCode, 
  MapPin, 
  Wallet,
  Users,
  BarChart3,
  Settings,
  HelpCircle,
  Bell,
  User,
  ChevronLeft,
  ChevronRight,
  LogOut
} from 'lucide-react'
import { SimpleLanguageSelector } from '@/components/SimpleLanguageSelector'
import { useAuthStore } from '@/stores/authStore'
import { cn } from '@/lib/utils'
import { notificationApi } from '@/services/api'
import { NotificationDropdown } from '@/components/navigation/NotificationDropdown'

const navigationItems = [
  { id: 'dashboard', labelKey: 'navigation.dashboard', icon: Home, path: '/dashboard' },
  { id: 'wallet', labelKey: 'navigation.wallet', icon: Wallet, path: '/wallet' },
  { id: 'qr-scanner', labelKey: 'navigation.qrScanner', icon: QrCode, path: '/qr-scanner' },
  { id: 'merchants', labelKey: 'navigation.merchants', icon: MapPin, path: '/merchants' },
  { id: 'contacts', labelKey: 'navigation.contacts', icon: Users, path: '/contacts' }
]

interface GlobalHeaderProps {
  className?: string
  showNavigation?: boolean
}

export function GlobalHeader({ className, showNavigation = true }: GlobalHeaderProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const notificationMenuRef = useRef<HTMLDivElement>(null)

  // Fetch unread count - count unique notifications (ignore duplicates from different languages)
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language || 'pt'
  const { data: unreadCountData } = useQuery(
    ['unread-count', currentLanguage],
    async () => {
      try {
        const response = await notificationApi.getNotifications()
        const data = response.data as any
        let allNotifications = []
        if (data?.results && Array.isArray(data.results)) {
          allNotifications = data.results
        } else if (data?.data && Array.isArray(data.data)) {
          allNotifications = data.data
        } else if (Array.isArray(data)) {
          allNotifications = data
        }
        
        // Filter notifications to show only the current language (same as NotificationDropdown)
        const normalizedCurrent = currentLanguage.split('-')[0].toLowerCase()
        const filteredNotifications = allNotifications.filter((n: any) => {
          const notificationLang = n.language || 'pt'
          const normalizedLang = notificationLang.split('-')[0].toLowerCase()
          return normalizedLang === normalizedCurrent
        })
        
        // Filter unread notifications
        const unreadNotifications = filteredNotifications.filter((n: any) => !n.is_read)
        
        // Group by code and params or transaction_id to count unique notifications
        const uniqueNotifications = new Map<string, any>()
        
        unreadNotifications.forEach((n: any) => {
          // Use code and params as key (for new format)
          if (n.code && n.params) {
            const key = `${n.code}_${JSON.stringify(n.params)}`
            if (!uniqueNotifications.has(key)) {
              uniqueNotifications.set(key, n)
            }
          }
          // Use transaction_id as key if available
          else if (n.data?.transaction_id) {
            const key = `transaction_${n.data.transaction_id}`
            if (!uniqueNotifications.has(key)) {
              uniqueNotifications.set(key, n)
            }
          } else if (n.data?.amount && n.data?.currency) {
            // Use amount + currency + timestamp (rounded to minute) as key
            const timestamp = new Date(n.created_at).getTime()
            const roundedTimestamp = Math.floor(timestamp / 60000) * 60000 // Round to minute
            const key = `payment_${n.data.amount}_${n.data.currency}_${roundedTimestamp}`
            if (!uniqueNotifications.has(key)) {
              uniqueNotifications.set(key, n)
            }
          } else {
            // Use id as fallback
            uniqueNotifications.set(n.id, n)
          }
        })
        
        return { unread_count: uniqueNotifications.size }
      } catch (error) {
        console.error('[GlobalHeader] Error fetching unread count:', error)
        return { unread_count: 0 }
      }
    },
    { 
      refetchInterval: 10000, // Refetch every 10 seconds
      enabled: !!user, // Only fetch if user is logged in
      retry: false // Don't retry on error to avoid loops
    }
  )

  const unreadCount = unreadCountData?.unread_count || 0


  const handleNavigation = (path: string) => {
    navigate(path)
    setIsUserMenuOpen(false)
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
    setIsUserMenuOpen(false)
  }

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen)
  }

  const userMenuItems = [
    { id: 'transactions', labelKey: 'navigation.transactions', icon: CreditCard, path: '/transactions' },
    { id: 'reports', labelKey: 'navigation.reports', icon: BarChart3, path: '/reports' },
    { id: 'settings', labelKey: 'navigation.settings', icon: Settings, path: '/settings' },
    { id: 'support', labelKey: 'navigation.support', icon: HelpCircle, path: '/support' },
    { id: 'logout', labelKey: 'navigation.logout', icon: LogOut, action: handleLogout }
  ]

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' })
    }
  }

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' })
    }
  }

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth)
    }
  }

  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      checkScrollButtons()
      container.addEventListener('scroll', checkScrollButtons)
      return () => container.removeEventListener('scroll', checkScrollButtons)
    }
  }, [user])

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false)
      }
      if (notificationMenuRef.current && !notificationMenuRef.current.contains(event.target as Node)) {
        const notificationButton = (event.target as HTMLElement)?.closest('button')
        if (!notificationButton?.querySelector('svg')) {
          setIsNotificationMenuOpen(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])


  return (
    <div className={cn(
      "fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-200",
      "safe-area-inset-top",
      className
    )}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Single row with everything */}
        <div className="flex items-center h-12">
          {/* Logo - Fixed position */}
          <div className="flex items-center flex-shrink-0 mr-4">
            <img 
              src="/assets/djelo-sem-fundo.png" 
              alt="djelo" 
              className="h-6 w-6"
            />
          </div>

          {/* Navigation with scroll arrows - Takes remaining space */}
          {showNavigation && user ? (
            <div className="flex items-center justify-center flex-1 min-w-0">
              {/* Scrollable navigation */}
              <div 
                ref={scrollContainerRef}
                className="flex overflow-x-auto scrollbar-hide justify-center"
              >
                <nav className="flex items-center space-x-1 min-w-max">
                  {navigationItems.map((item) => {
                    const isActive = location.pathname === item.path
                    const IconComponent = item.icon

                    return (
                      <motion.button
                        key={item.id}
                        onClick={() => handleNavigation(item.path)}
                        className={cn(
                          "flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap",
                          "hover:bg-gray-50",
                          isActive 
                            ? 'bg-primary text-white hover:bg-primary/90' 
                            : 'text-text-secondary hover:text-primary'
                        )}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <IconComponent className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span className="hidden sm:inline">{t(item.labelKey || '')}</span>
                      </motion.button>
                    )
                  })}
                </nav>
              </div>
            </div>
          ) : (
            /* Spacer when no navigation */
            <div className="flex-1" />
          )}

          {/* Right side - Language Selector and User Actions */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            {/* Language Selector */}
            <SimpleLanguageSelector className="scale-90" />
            
            {/* User Actions (only show if user is logged in) */}
            {user && (
              <>
                {/* Notifications */}
                <div className="relative z-[100]" ref={notificationMenuRef}>
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setIsNotificationMenuOpen(!isNotificationMenuOpen)
                    }}
                    className="relative p-2 text-text-secondary hover:text-primary transition-colors"
                    type="button"
                  >
                    <Bell className="h-4 w-4" />
                    {unreadCount > 0 && (
                      <span className="absolute top-0 right-0 w-4 h-4 bg-error text-white text-[10px] rounded-full flex items-center justify-center font-semibold">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  <NotificationDropdown
                    isOpen={isNotificationMenuOpen}
                    onClose={() => setIsNotificationMenuOpen(false)}
                  />
                </div>

                {/* User menu */}
                <div className="flex items-center space-x-2 relative" ref={userMenuRef}>
                  <div className="text-right hidden sm:block">
                    <p className="text-xs font-medium text-text-primary">
                      {user?.profile?.first_name} {user?.profile?.last_name}
                    </p>
                    <p className="text-xs text-text-secondary">
                      {user?.email}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      toggleUserMenu()
                    }}
                    className="p-2 text-text-secondary hover:text-primary transition-colors"
                    title="Menu do usuário"
                  >
                    <User className="h-4 w-4" />
                  </button>

                  {/* User Dropdown Menu */}
                  <AnimatePresence>
                    {isUserMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                      >
                        {userMenuItems.map((item) => {
                          const IconComponent = item.icon
                          const isActive = location.pathname === item.path

                          return (
                            <button
                              key={item.id}
                              onClick={() => {
                                if (item.action) {
                                  item.action()
                                } else if (item.path) {
                                  handleNavigation(item.path)
                                }
                              }}
                              className={`w-full flex items-center px-4 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                                isActive ? 'text-primary bg-primary bg-opacity-10' : 'text-text-primary'
                              }`}
                            >
                              <IconComponent className="h-4 w-4 mr-3" />
                              {t(item.labelKey)}
                            </button>
                          )
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
